// SCR-TABLET-001 — حالت تحویل تبلت (US-DOC-031، 041؛ FLOW-09): تمام‌صفحه و بدون منو و ناوبری سامانه، فقط فرم‌ها و رضایت‌نامه‌های همین جلسه به
// ترتیب؛ پایان ← صفحه «تحویل به پذیرش»؛ خروج فقط با PIN کارکن (پنج PIN نادرست ← قفل — BR-DOC-031، C-AGENT-504).
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { isApiError, unwrap, type DocConsentDetail, type DocFormSubmissionDetail, type DocTabletItemView, type DocTabletSessionView } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { type Answers, asDefinition, cleanAnswers, ErrorState, faDigits, FormRenderer, LoadingState, missingRequired, SampleBadge } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { docKeys, num, SCR_TABLET_001 } from './common';
import { SignPreparedConsent } from './PatientSignedConsentsPanel';

export interface TabletLocationState {
  values?: Record<string, Record<string, string>>;
}

function TabletForm({ session, item, onDone }: { session: DocTabletSessionView; item: DocTabletItemView; onDone: () => void }) {
  const { t } = useTranslation('doc');
  const { tenantApi } = useTenantSession();
  const [detail, setDetail] = useState<DocFormSubmissionDetail | null>(null);
  const [answers, setAnswers] = useState<Answers>({});
  const [missing, setMissing] = useState<string[]>([]);
  const start = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/documents/form-submissions', { body: { templateId: item.templateId, patientId: session.patientId, appointmentId: session.appointmentId, encounterId: null, tabletSessionId: session.id } })),
    onSuccess: setDetail,
  });
  const { mutate: begin } = start;
  useEffect(() => begin(), [begin, item.templateId]);
  const definition = asDefinition(detail?.template.definition);
  const patient = { gender: detail?.patient.gender, age: detail?.patient.age };
  const submit = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/documents/form-submissions/{id}:submit', { params: { path: { id: detail!.submission.id } }, body: { answers: cleanAnswers(definition, answers, patient) as never } })),
    onSuccess: onDone,
  });
  if (start.error) {
    return <ErrorState error={start.error} onRetry={() => start.mutate()} />;
  }
  if (!detail) {
    return <LoadingState rows={4} />;
  }
  return (
    <Stack spacing={2} data-testid="tablet-form">
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography variant="h5" component="h2">
          {detail.submission.templateName}
        </Typography>
        {item.isSample ? <SampleBadge /> : null}
      </Stack>
      <FormRenderer definition={definition} answers={answers} onChange={setAnswers} patient={patient} errors={missing} testPrefix="tablet-field" />
      {missing.length > 0 ? <Alert severity="error">{t('doc:forms.missing', { count: missing.length })}</Alert> : null}
      {submit.error ? <ErrorState error={submit.error} compact /> : null}
      <Button
        size="large"
        variant="contained"
        disabled={submit.isPending}
        data-testid="tablet-form-submit"
        onClick={() => {
          const required = missingRequired(definition, answers, patient);
          setMissing(required);
          if (required.length === 0) {
            submit.mutate();
          }
        }}
      >
        {t('doc:tablet.next')}
      </Button>
    </Stack>
  );
}

function TabletConsent({ session, item, values, onDone }: { session: DocTabletSessionView; item: DocTabletItemView; values: Record<string, string>; onDone: () => void }) {
  const { tenantApi } = useTenantSession();
  const prepare = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/documents/consents', { body: { templateId: item.templateId, patientId: session.patientId, appointmentId: session.appointmentId, values, providerId: null, serviceId: null, tabletSessionId: session.id } })),
  });
  const { mutate: begin } = prepare;
  useEffect(() => begin(), [begin, item.templateId]);
  if (prepare.error) {
    return <ErrorState error={prepare.error} onRetry={() => prepare.mutate()} />;
  }
  const detail: DocConsentDetail | undefined = prepare.data;
  if (!detail) {
    return <LoadingState rows={4} />;
  }
  return (
    <Box data-testid="tablet-consent">
      <SignPreparedConsent detail={detail} onSigned={onDone} />
    </Box>
  );
}

function ExitDialog({ sessionId, onExited, onClose }: { sessionId: string; onExited: () => void; onClose: () => void }) {
  const { t } = useTranslation('doc');
  const { tenantApi } = useTenantSession();
  const [pin, setPin] = useState('');
  const exit = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/documents/tablet-sessions/{id}:exit', { params: { path: { id: sessionId } }, body: { pin } })),
    onSuccess: onExited,
  });
  const remaining = isApiError(exit.error) ? (exit.error.extensions.remaining as number | undefined) : undefined;
  return (
    <Dialog open onClose={onClose} aria-labelledby="tablet-exit-title">
      <DialogTitle id="tablet-exit-title">{t('doc:tablet.exit_title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography>{t('doc:tablet.exit_hint')}</Typography>
          <TextField
            autoFocus
            type="password"
            label={t('doc:tablet.pin')}
            value={pin}
            onChange={(event) => setPin(event.target.value.replace(/\D/g, ''))}
            slotProps={{ htmlInput: { 'data-testid': 'tablet-exit-pin', inputMode: 'numeric', maxLength: 8, autoComplete: 'off' } }}
          />
          {exit.error ? (
            <Alert severity="error" data-testid="tablet-exit-error">
              {remaining !== undefined ? t('doc:tablet.pin_wrong', { remaining: faDigits(remaining) }) : null}
              <ErrorState error={exit.error} compact />
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('doc:actions.cancel')}</Button>
        <Button variant="contained" onClick={() => exit.mutate()} disabled={pin.length < 4 || exit.isPending} data-testid="tablet-exit-confirm">
          {t('doc:tablet.exit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export interface TabletModePageProps {
  /** جلسه (تبلت جفت‌شده با حساب دستگاه — AP-IAM-05)؛ تهی ← از مسیر. */
  sessionId?: string;
  /** پس از خروج با PIN (تبلت جفت‌شده: بازگشت به انتظار جلسه بعد). */
  onExited?: () => void;
}

export function TabletModePage({ sessionId, onExited }: TabletModePageProps = {}) {
  const { t } = useTranslation('doc');
  const params = useParams();
  const id = sessionId ?? params.id ?? '';
  const { tenantApi, session: tenant } = useTenantSession();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [exiting, setExiting] = useState(false);
  const values = (location.state as TabletLocationState | null)?.values ?? {};
  const session = useQuery({
    queryKey: docKeys.tablet(tenant?.tenantId, id),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/documents/tablet-sessions/{id}', { params: { path: { id } } })),
  });

  // قفل ناوبری: دکمه «بازگشت» مرورگر و بستن صفحه در حالت تبلت بی‌اثر است؛ خروج فقط با PIN (AC-DOC-031.1).
  useEffect(() => {
    const block = () => window.history.pushState(null, '', window.location.href);
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', block);
    const beforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', beforeUnload);
    void document.documentElement.requestFullscreen?.().catch(() => undefined);
    return () => {
      window.removeEventListener('popstate', block);
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, []);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: docKeys.tablet(tenant?.tenantId, id) });
  const data = session.data;
  const items = data?.items ?? [];
  const done = items.filter((item) => item.resultId).length;
  const current = items.find((item) => !item.resultId);
  const exited = () => {
    void document.exitFullscreen?.().catch(() => undefined);
    if (onExited) {
      onExited();
      return;
    }
    navigate(data ? `/patients/${data.patientId}?tab=forms` : '/', { replace: true });
  };

  return (
    <Box data-screen-id={SCR_TABLET_001} data-testid="tablet-mode" sx={{ position: 'fixed', inset: 0, bgcolor: 'background.default', overflowY: 'auto', zIndex: (theme) => theme.zIndex.modal - 1 }}>
      <Stack direction="row" sx={{ p: 1, alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
        <LockOutlinedIcon color="action" />
        <Typography sx={{ flexGrow: 1, mx: 1 }}>{t('doc:tablet.title')}</Typography>
        <Button size="small" onClick={() => setExiting(true)} data-testid="tablet-exit">
          {t('doc:tablet.staff_exit')}
        </Button>
      </Stack>
      <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, md: 3 } }}>
        {session.isPending ? <LoadingState rows={4} /> : null}
        {session.error ? <ErrorState error={session.error} /> : null}
        {data ? (
          <Stack spacing={2}>
            <LinearProgress variant="determinate" value={items.length ? (done / items.length) * 100 : 0} aria-label={t('doc:tablet.progress')} />
            <Typography variant="body2" color="text.secondary" data-testid="tablet-progress">
              {t('doc:tablet.step', { current: faDigits(Math.min(done + 1, items.length)), total: faDigits(items.length) })}
            </Typography>
            {data.status === 'Exited' ? <Alert severity="info">{t('doc:tablet.exited')}</Alert> : null}
            {data.status !== 'Exited' && current ? (
              current.kind === 'Form' ? (
                <TabletForm key={current.templateId} session={data} item={current} onDone={refresh} />
              ) : (
                <TabletConsent key={current.templateId} session={data} item={current} values={values[current.templateId] ?? {}} onDone={refresh} />
              )
            ) : null}
            {data.status === 'Completed' || (!current && data.status !== 'Exited') ? (
              <Paper sx={{ p: 4, textAlign: 'center' }} data-testid="tablet-handback">
                <Typography variant="h4" component="h2" gutterBottom>
                  {t('doc:tablet.thanks')}
                </Typography>
                <Typography>{t('doc:tablet.handback')}</Typography>
              </Paper>
            ) : null}
            <Typography variant="caption" color="text.secondary">
              {t('doc:tablet.attempts', { remaining: faDigits(num(data.remainingPinAttempts)) })}
            </Typography>
          </Stack>
        ) : null}
      </Box>
      {exiting ? <ExitDialog sessionId={id} onExited={exited} onClose={() => { setExiting(false); refresh(); }} /> : null}
    </Box>
  );
}
